import { useState, useEffect } from "react";
import { CreditCard, Loader2, Users, Link2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

export default function AdminGateways() {
  const [gateways, setGateways] = useState(null);
  const [saving, setSaving] = useState("");

  const load = () => api.get("/admin/gateways").then(({ data }) => setGateways(data)).catch(() => setGateways([]));
  useEffect(() => { load(); }, []);

  const toggle = async (id, enabled) => {
    setSaving(id);
    try {
      await api.put(`/admin/gateways/${id}`, { enabled });
      setGateways((prev) => prev.map((g) => (g.id === id ? { ...g, enabled } : g)));
      toast.success(enabled ? "Gateway ativado" : "Gateway desativado");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(""); }
  };

  if (!gateways) return <div className="text-muted-foreground">Carregando...</div>;

  return (
    <div className="max-w-3xl space-y-6" data-testid="admin-gateways">
      <div>
        <h2 className="font-heading text-xl font-bold tracking-tight sm:text-2xl">Gateways de pagamento</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Ative os gateways que os revendedores poderão usar para receber os pagamentos das suas lojas.
          Novos provedores aparecerão aqui conforme forem adicionados.
        </p>
      </div>

      <div className="space-y-4">
        {gateways.map((g) => (
          <div key={g.id} className="rounded-xl border border-border bg-card p-5" data-testid={`gateway-card-${g.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <CreditCard className="h-5 w-5" />
                </span>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-heading font-semibold">{g.name}</h3>
                    {g.enabled
                      ? <Badge className="border-green-500/30 bg-green-500/15 text-green-600 dark:text-green-400" variant="outline">Ativo</Badge>
                      : <Badge variant="secondary">Inativo</Badge>}
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground">{g.description}</p>
                  <div className="mt-3 flex flex-wrap gap-4 text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1.5"><Link2 className="h-3.5 w-3.5" /> {(g.methods || []).join(", ").toUpperCase()}</span>
                    <span className="inline-flex items-center gap-1.5"><Users className="h-3.5 w-3.5" /> {g.resellers_using} usando · {g.resellers_configured} configurados</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {saving === g.id && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                <Switch checked={g.enabled} onCheckedChange={(v) => toggle(g.id, v)} data-testid={`gateway-toggle-${g.id}`} />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
